import React, { useState } from 'react';
import { Bell, Plus, Loader2, CalendarClock, X, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { situacaoDoAviso, type AteQuando, type AvisoUc, type SituacaoAviso } from '../../../../src/lib/avisosUc';
import { alterarPrazoDoAviso, encerrarAviso, type AvisoComCiencia } from '../../../../src/services/avisosUc';
import AvisoCard from './AvisoCard';
import EncerrarAvisoModal from './EncerrarAvisoModal';
import PrazoAvisoModal from './PrazoAvisoModal';
import AgendarLembreteModal, { type UcDoAviso } from '../AgendarLembreteModal';

// Aba "Avisos" do modal da UC: todos os avisos para o faturamento, um card por aviso
// (antes era um card por mês, no topo de todas as abas). Os dados vêm de useAvisosUc,
// no modal, porque a faixa do topo usa a mesma lista.
interface Props {
    uc: UcDoAviso & { desconto_tarifa?: number | string | null; desconto_bandeira?: number | string | null };
    avisos: AvisoComCiencia[];
    mesAtual: number;
    carregando: boolean;
    erro: string | null;
    recarregar: () => void;
}

const pct = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : `${Math.round(Number(v) * 1000) / 10}%`);

const SECOES: { situacao: SituacaoAviso; titulo: string; ajuda: string }[] = [
    { situacao: 'ativo', titulo: 'Valendo agora', ajuda: 'Aparecem no topo da validação, do cálculo e da emissão.' },
    { situacao: 'futuro', titulo: 'Começam depois', ajuda: '' },
    { situacao: 'terminado', titulo: 'Prazo terminou', ajuda: 'Não aparecem mais no cálculo. Se ainda valem, altere o prazo.' },
    { situacao: 'encerrado', titulo: 'Encerrados', ajuda: '' },
];

const BOTAO = 'px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50';

const AvisosUcTab: React.FC<Props> = ({ uc, avisos, mesAtual, carregando, erro, recarregar }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [novoAberto, setNovoAberto] = useState(false);
    const [encerrando, setEncerrando] = useState<AvisoUc | null>(null);
    const [prazo, setPrazo] = useState<AvisoUc | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [verEncerrados, setVerEncerrados] = useState(false);

    const autor = { id: user?.id ?? null, nome: profile?.full_name || user?.user_metadata?.full_name || user?.email || 'Usuário' };
    const descontoCadastro = `Tarifa ${pct(uc.desconto_tarifa)} · Bandeira ${pct(uc.desconto_bandeira)}`;

    const porSituacao = (s: SituacaoAviso) => avisos.filter(a => situacaoDoAviso(a, mesAtual) === s);

    const confirmarEncerrar = async (motivo: string) => {
        if (!encerrando) return;
        setSalvando(true);
        try {
            await encerrarAviso(supabase, encerrando.id, motivo, autor);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'encerrar_aviso', origem: 'acao' });
            addToast('Aviso encerrado.', 'success');
            setEncerrando(null);
            recarregar();
        } catch (e) {
            console.error(e);
            addToast('Erro ao encerrar o aviso.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    const confirmarPrazo = async (inicio: string, ate: AteQuando) => {
        if (!prazo) return;
        setSalvando(true);
        try {
            await alterarPrazoDoAviso(supabase, prazo, inicio, ate);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'prazo_aviso', origem: 'acao' });
            addToast('Prazo do aviso atualizado.', 'success');
            setPrazo(null);
            recarregar();
        } catch (e: any) {
            console.error(e);
            addToast(e?.message || 'Erro ao alterar o prazo.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Bell size={18} className="text-orange-500" /> Avisos para o faturamento
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
                        Instruções para quem valida, calcula e emite a fatura desta UC. O que vale todo mês e o sistema já sabe aplicar
                        sozinho fica no cadastro (aba Dados Cadastrais): <strong>desconto</strong>, <strong>pagador do boleto</strong> e <strong>vencimento</strong>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setNovoAberto(true)}
                    className="shrink-0 px-4 py-2 bg-primary hover:bg-primary-dark text-white font-semibold text-sm rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={16} /> Novo aviso
                </button>
            </div>

            {carregando && avisos.length === 0 ? (
                <div className="flex justify-center p-6"><Loader2 className="animate-spin text-primary" size={24} /></div>
            ) : erro ? (
                <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>
            ) : avisos.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                    Nenhum aviso para esta UC.
                </p>
            ) : (
                SECOES.map(secao => {
                    const lista = porSituacao(secao.situacao);
                    if (lista.length === 0) return null;
                    const recolhida = secao.situacao === 'encerrado' && !verEncerrados;
                    return (
                        <section key={secao.situacao} data-secao={secao.situacao} className="space-y-2">
                            <button
                                type="button"
                                disabled={secao.situacao !== 'encerrado'}
                                onClick={() => setVerEncerrados(v => !v)}
                                className="flex items-center gap-1.5 text-sm font-bold text-gray-700 dark:text-gray-200 disabled:cursor-default"
                            >
                                {secao.situacao === 'encerrado' && (recolhida ? <ChevronRight size={16} /> : <ChevronDown size={16} />)}
                                {secao.titulo} ({lista.length})
                            </button>
                            {secao.ajuda && !recolhida && <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">{secao.ajuda}</p>}
                            {!recolhida && (
                                <div className="space-y-2">
                                    {lista.map(aviso => (
                                        <AvisoCard
                                            key={aviso.id}
                                            aviso={aviso}
                                            situacao={secao.situacao}
                                            ciencia={aviso.ultimaCiencia}
                                            descontoCadastro={descontoCadastro}
                                            acoes={secao.situacao === 'encerrado' ? undefined : (
                                                <>
                                                    <button type="button" className={BOTAO} onClick={() => setPrazo(aviso)}>
                                                        <CalendarClock size={15} /> {secao.situacao === 'terminado' ? 'Renovar' : 'Alterar prazo'}
                                                    </button>
                                                    <button type="button" className={BOTAO} onClick={() => setEncerrando(aviso)}>
                                                        <X size={15} /> Encerrar
                                                    </button>
                                                </>
                                            )}
                                        />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })
            )}

            <EncerrarAvisoModal aviso={encerrando} salvando={salvando} onCancelar={() => setEncerrando(null)} onConfirmar={confirmarEncerrar} />
            <PrazoAvisoModal aviso={prazo} salvando={salvando} onCancelar={() => setPrazo(null)} onConfirmar={confirmarPrazo} />
            <AgendarLembreteModal
                isOpen={novoAberto}
                onClose={() => setNovoAberto(false)}
                onSuccess={recarregar}
                ucInicial={uc}
            />
        </div>
    );
};

export default AvisosUcTab;
